import {
  Alert,
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import {
  LotStatus, MoldingMaterials, PartTypes, PPartStatus,
} from '@parallel-fluidics/shared/constants';
import { allocateBomConsumption } from '@parallel-fluidics/shared/models/lots';
import PPartRefBadge from 'components/Projects/pPartRefBadge';
import assembleLot from 'features/lots/api/assembleLot';
import fetchLot from 'features/lots/api/fetchLot';
import { useInvalidateLot } from 'features/lots/hooks/useLot';
import { useInvalidateLotsQuery } from 'features/lots/hooks/useLotsQuery';
import { useInvalidateLotsUsingSource } from 'features/lots/hooks/useLotsUsingSource';
import getPPartOption from 'features/pPart/utils/getPPartOption';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import { useFormik } from 'formik';
import { useGetProjectParts } from 'hooks/use-basicQueries';
import useProjects from 'hooks/use-projects';
import { useUserData } from 'providers/userDataProvider';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import AssembleSourcesTable from './components/AssembleSourcesTable';

/**
 * Assemble a new lot from scanned source bags: pick what's being made and
 * how many, scan every bag consumed, create. Scans are the record (strict
 * graph). Only parts with a bill of materials can be assembled — the BOM
 * is what lets each source bag's consumption be tracked, so a project
 * without one gets a warning pointing at its parts page instead of a
 * picker. Every BOM line must be covered by enough scanned quantity
 * before Create enables (and the transaction re-checks against fresh
 * reads). Bags not on the BOM only warn. Quarantined and
 * awaiting-inspection bags are rejected at scan time; Empty bags warn but
 * are allowed (counts are advisory); sealed bags open automatically.
 */
export default function AssembleLotPage() {
  const navigate = useNavigate();
  const { user } = useUserData();
  const invalidateLotsList = useInvalidateLotsQuery();
  const invalidateLot = useInvalidateLot();
  const invalidateLotsUsingSource = useInvalidateLotsUsingSource();
  const [scanError, setScanError] = useState('');

  const {
    values,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: {
      projectOption: null,
      partOption: null,
      qty: '',
      material: '',
      sources: [],
    },
    onSubmit: async (submitValues) => {
      try {
        const { id, code } = await assembleLot({
          projectDocId: submitValues.projectOption.id,
          pPartDocId: submitValues.partOption.value,
          material: submitValues.material.trim() || null,
          initialQty: Number(submitValues.qty),
          sourceLotIds: submitValues.sources.map((source) => source.id),
          user: { uid: user.id, email: user.email },
        });
        toast.success(`Lot ${code} assembled`);
        invalidateLotsList();
        // The transaction also decremented (and possibly opened/emptied)
        // every scanned source and added this lot to their genealogy —
        // drop those caches so open source-lot pages refetch.
        submitValues.sources.forEach((source) => {
          invalidateLot(source.id);
          invalidateLotsUsingSource(source.id);
        });
        navigate(`/lots/${id}`);
      } catch (error) {
        toast.error(`Could not assemble lot: ${error.message}`);
      }
    },
  });

  const { projectOptions, isLoading: projectsLoading } = useProjects({ isInternal: true });
  const { data: parts, isLoading: partsLoading } = useGetProjectParts({
    projectDocId: values.projectOption?.id,
    // Lots are bags of consumables — reusable tooling can't be assembled.
    excludeTypes: [PartTypes.TOOL, PartTypes.PISTON],
    status: PPartStatus.ACTIVE,
    disabled: !values.projectOption,
  });

  // Only parts with a BOM are assemblable; the hint says how many of the
  // project's parts that leaves so a missing BOM is obvious, not a mystery.
  const partOptions = useMemo(() => (parts || [])
    .filter((part) => part.components?.length > 0)
    .map((part) => ({
      ...getPPartOption(part),
      material: part.material || '',
      components: part.components,
    })), [parts]);
  const partsLoaded = Boolean(values.projectOption) && !partsLoading && parts !== undefined;
  const noAssemblableParts = partsLoaded && partOptions.length === 0;

  // The output part's BOM: hard coverage rule, plus the live allocation
  // shown in the table ("this assembly uses X of Y").
  const bom = useMemo(() => values.partOption?.components || [], [values.partOption]);
  const qtyNumber = Number(values.qty);
  const { lines, deltaByLotId } = useMemo(
    () => allocateBomConsumption(qtyNumber > 0 ? qtyNumber : 0, bom, values.sources),
    [qtyNumber, bom, values.sources],
  );
  const shortLines = qtyNumber > 0 ? lines.filter((line) => line.short > 0) : [];

  const canCreate = values.projectOption && values.partOption
    && Number.isInteger(qtyNumber) && qtyNumber > 0 && values.sources.length > 0
    && bom.length > 0 && shortLines.length === 0;

  const handleScan = async ({ id }) => {
    setScanError('');
    try {
      const lot = await fetchLot(id);
      if ([LotStatus.QUARANTINED, LotStatus.AWAITING_INSPECTION].includes(lot.status)) {
        setScanError(`${lot.code} is ${lot.status} and can't be consumed`);
        return;
      }
      if (lot.status === LotStatus.EMPTY) {
        toast.warning(`${lot.code} is marked Empty — its count may be off`);
      }
      // Functional update so rapid back-to-back scans can't drop a bag.
      setValues((prev) => (prev.sources.some((source) => source.id === id)
        ? prev
        : { ...prev, sources: [...prev.sources, lot] }));
    } catch (error) {
      setScanError(error.message);
    }
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!canCreate}
              >
                Create lot
              </Button>
            )}
          >
            Assemble lot
          </Header>
        )}
      >
        <SpaceBetween size="m">
          <ColumnLayout columns={3}>
            <FormField label="Internal Project">
              <Select
                selectedOption={values.projectOption}
                options={projectOptions}
                statusType={projectsLoading ? 'loading' : 'finished'}
                filteringType="auto"
                placeholder="Select a project"
                onChange={({ detail }) => {
                  setFieldValue('projectOption', detail.selectedOption);
                  setFieldValue('partOption', null);
                  setFieldValue('material', '');
                  // Scanned bags belong to the previous output — start over.
                  setFieldValue('sources', []);
                  setScanError('');
                }}
              />
            </FormField>
            <FormField
              label="Output part"
              // constraintText sits under the control, so the three
              // inputs stay on one line (a description would push this
              // select down).
              constraintText={partsLoaded && !noAssemblableParts
                ? `Only parts with a bill of materials — ${partOptions.length} of ${parts.length} in this project.`
                : 'Only parts with a bill of materials.'}
            >
              <Select
                selectedOption={values.partOption}
                options={partOptions}
                statusType={partsLoading && values.projectOption ? 'loading' : 'finished'}
                filteringType="auto"
                placeholder={noAssemblableParts ? 'No parts to assemble' : 'Select a part'}
                disabled={!values.projectOption || noAssemblableParts}
                onChange={({ detail }) => {
                  setFieldValue('partOption', detail.selectedOption);
                  setFieldValue('material', detail.selectedOption.material || '');
                  // Scanned bags belong to the previous output — start over.
                  setFieldValue('sources', []);
                  setScanError('');
                }}
              />
            </FormField>
            <FormField
              label="Quantity assembled"
              // Say why Create is disabled instead of silently blocking —
              // an empty (untouched) field shows no error.
              errorText={values.qty !== '' && !(Number.isInteger(qtyNumber) && qtyNumber > 0)
                ? 'Quantity must be a positive whole number' : null}
            >
              <Input
                type="number"
                value={values.qty}
                onChange={({ detail }) => setFieldValue('qty', detail.value)}
              />
            </FormField>
          </ColumnLayout>
          {noAssemblableParts && (
            <Alert
              type="warning"
              header={`No part in ${values.projectOption.value} has a bill of materials`}
              action={(
                <Button onClick={() => navigate(`/projects/${values.projectOption.id}`)}>
                  Open project parts
                </Button>
              )}
            >
              An assembled part needs a BOM so Parascope knows which bags to consume. Add one
              on the part&apos;s page, then come back here.
            </Alert>
          )}
          <FormField label="Material" description="Prefilled from the part — shown on the label.">
            <Autosuggest
              value={values.material}
              onChange={({ detail }) => setFieldValue('material', detail.value)}
              options={Object.values(MoldingMaterials).map((value) => ({ value }))}
              placeholder="Type or select a value"
              empty="No options found"
            />
          </FormField>
          {bom.length > 0 && (
            <FormField
              label="Bill of materials"
              description="Per unit assembled — every line must be covered by scanned bags."
            >
              <SpaceBetween size="xxs" direction="horizontal">
                {bom.map((line) => (
                  <PPartRefBadge
                    key={`${line.projectDocId}/${line.pPartDocId}`}
                    projectDocId={line.projectDocId}
                    pPartDocId={line.pPartDocId}
                    quantity={line.quantity}
                  />
                ))}
              </SpaceBetween>
            </FormField>
          )}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Source bags</Header>}>
        <SpaceBetween size="m">
          <ScanField
            id="assemble-scanner"
            accept={[ScanTypes.LOT]}
            onScan={handleScan}
            errorText={scanError}
            autoFocus
            description="Sealed bags open automatically when the lot is created."
          />
          {shortLines.length > 0 && (
            <Alert type="warning" header="BOM lines not covered — Create is disabled until every line has enough parts">
              <SpaceBetween size="xxs">
                {shortLines.map((line) => (
                  <div
                    key={`${line.projectDocId}/${line.pPartDocId}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                    }}
                  >
                    <PPartRefBadge
                      projectDocId={line.projectDocId}
                      pPartDocId={line.pPartDocId}
                      quantity={line.quantity}
                    />
                    <Box>
                      {`needs ${line.needed}, scanned bags cover ${line.needed - line.short} — short by ${line.short}`}
                    </Box>
                  </div>
                ))}
              </SpaceBetween>
            </Alert>
          )}
          <AssembleSourcesTable
            sources={values.sources}
            bom={bom}
            deltaByLotId={deltaByLotId}
            // Functional update, like handleScan — a remove racing an
            // in-flight scan must not commit a stale sources list.
            onRemove={(id) => setValues((prev) => ({
              ...prev,
              sources: prev.sources.filter((source) => source.id !== id),
            }))}
          />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
